/** @jsxImportSource @emotion/react */
import { css, Interpolation, Theme } from "@emotion/react";
import { EuiIcon, EuiIconProps, EuiLoadingSpinner } from "@inscopix/ideas-eui";
import { useProjectPermissionStore } from "pages/project/ProjectPermissionProvider";
import { ButtonHTMLAttributes, ClassAttributes } from "react";
import { UserPermission } from "types/UserAccessLevels";
import { DATA_TABLE_TOOLTIP_ID } from "../layout/DataTableTooltip";
import ReactDOMServer from "react-dom/server";

const styles = {
  button: css`
    inline-size: 24px;
    block-size: 24px;
    border-radius: 4px;
    display: inline;
    line-height: 12px;
  `,
};

type ButtonIconPlainProps = ClassAttributes<HTMLButtonElement> &
  ButtonHTMLAttributes<HTMLButtonElement> & {
    css?: Interpolation<Theme>;
  } & {
    "aria-label": string;
    color: EuiIconProps["color"];
    iconSize: EuiIconProps["size"];
    iconType: EuiIconProps["type"];
    isLoading?: boolean;
  };

export const ButtonIconPlain = ({
  color,
  iconSize,
  iconType,
  css,
  isLoading,
  ...buttonProps
}: ButtonIconPlainProps) => {
  return (
    <button css={[styles.button, css]} {...buttonProps}>
      {isLoading ? (
        <EuiLoadingSpinner
          size={iconSize === "original" ? undefined : iconSize}
        />
      ) : (
        <EuiIcon
          color={buttonProps.disabled ? "#a2abba" : color}
          size={iconSize}
          type={iconType}
        />
      )}
    </button>
  );
};

const defaultTooltip = ReactDOMServer.renderToStaticMarkup(
  <div
    style={{ display: "flex", alignItems: "center", flexDirection: "column" }}
  >
    {/* Lock icon */}
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 16 16"
      role="img"
      data-icon-type="lock"
      data-is-loaded="true"
      aria-hidden="true"
      fill="#ffd700"
    >
      <path d="M4 5v-.8C4 1.88 5.79 0 8 0s4 1.88 4 4.2V5h1.143c.473 0 .857.448.857 1v9c0 .552-.384 1-.857 1H2.857C2.384 16 2 15.552 2 15V6c0-.552.384-1 .857-1H4ZM3 15h10V6H3v9Zm5.998-3.706L9.5 12.5h-3l.502-1.206A1.644 1.644 0 0 1 6.5 10.1c0-.883.672-1.6 1.5-1.6s1.5.717 1.5 1.6c0 .475-.194.901-.502 1.194ZM11 4.36C11 2.504 9.657 1 8 1S5 2.504 5 4.36V5h6v-.64Z"></path>
    </svg>
    <span>You do not have permission to perform this action.</span>
  </div>,
);

interface ButtonIconPlainPermissionedProps extends ButtonIconPlainProps {
  requiredPermission: UserPermission;
}

export const ButtonIconPlainPermissioned = ({
  requiredPermission,
  ...props
}: ButtonIconPlainPermissionedProps) => {
  const isPermitted = useProjectPermissionStore(
    (s) => s.permissions[requiredPermission],
  );

  const tooltipHtml = isPermitted ? props["data-tooltip-html"] : defaultTooltip;

  return (
    <ButtonIconPlain
      {...props}
      disabled={!isPermitted || props.disabled}
      data-tooltip-id={DATA_TABLE_TOOLTIP_ID}
      data-tooltip-html={tooltipHtml}
    />
  );
};
